import { useEffect, useState } from "react";
import { AnswerOption, CreateQuestionInput, Question } from "../../API";
import { QueryStatus } from "../../app/types";
import {
  deleteQuestion as deleteQuestionAPI,
  createQuestion as createQuestionAPI,
  fetchQuestionsByInstrument
} from "./questionsAPI";

export const useDeleteQuestion = () => {
  const [deletedQuestion, setDeletedQuestion] = useState<Question | null>(null);
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const deleteQuestion = async (id: string) => {
    setStatus("loading");
    setError(null);

    try {
      const deletionResult = await deleteQuestionAPI(id);
      setDeletedQuestion(deletionResult);
      setStatus("succeeded");
      return deletionResult;
    } catch (error) {
      console.error("error in deleteQuestion:", error);
      const errorString = JSON.stringify(error);
      setStatus("failed");
      setError(errorString);
    }
    return null;
  };
  return {
    deletedQuestion,
    status,
    error,
    deleteQuestion
  };
};

export const useCreateQuestion = () => {
  const [createdQuestion, setCreatedQuestion] = useState<Question | null>(null);
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const createQuestion = async (input: CreateQuestionInput) => {
    setStatus("loading");
    setError(null);

    try {
      const creationResult = await createQuestionAPI(input);
      setCreatedQuestion(creationResult);
      setStatus("succeeded");
      return creationResult;
    } catch (error) {
      console.error("error in createQuestion:", error);
      const errorString = JSON.stringify(error);
      setStatus("failed");
      setError(errorString);
    }
    return null;
  };
  return {
    createdQuestion,
    status,
    error,
    createQuestion
  };
};

export const useQuestionsByInstrument = (instrumentId?: string | null) => {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const getQuestionsByInstrument = async (instrumentId: string) => {
    setStatus("loading");
    setError(null);

    try {
      const results = await fetchQuestionsByInstrument(instrumentId);
      const resultsMinusDeletedAnswerOptions = results?.map((question) => {
        const filteredItems =
          question.answerOptions?.items
            ?.filter((option) => !option?.deletedAt)
            .flatMap((option) => (option ? [option] : [])) ?? [];

        return {
          ...question,
          answerOptions: {
            ...question.answerOptions,
            __typename: "ModelAnswerOptionConnection" as const,
            items: filteredItems as (AnswerOption | null)[] // Cast to match expected type
          }
        };
      });
      setQuestions(resultsMinusDeletedAnswerOptions ?? []);
      setStatus("succeeded");
    } catch (error) {
      const errorString = `error fetching questions by instrument: ${JSON.stringify(
        error
      )}`;
      console.error(errorString);
      setError(errorString);
      setStatus("failed");
    }
  };

  useEffect(() => {
    if (instrumentId) {
      getQuestionsByInstrument(instrumentId);
    }
  }, [instrumentId]);

  return {
    questions,
    status,
    error
  };
};
